import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { PDF_THEMES, DEFAULT_THEME } from '../themes';
import PdfHeader from '../components/PdfHeader';
import PdfFooter from '../components/PdfFooter';
import PdfFilterLine from '../components/PdfFilterLine';
import PdfSummaryCards from '../components/PdfSummaryCards';
import { PdfTableHead, PdfTableRow, PdfTableFoot } from '../components/PdfTable';
import { fmtINR, fmtDate } from '../pdfUtils';

const s = StyleSheet.create({
  page:    { fontFamily: 'NotoSans', paddingBottom: 60 },
  content: { marginHorizontal: 40, marginTop: 12 },
  bankHeading: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 5,
    marginTop: 10,
  },
  bankHeadingText: { fontSize: 9, fontFamily: 'NotoSans', fontWeight: 700, color: '#FFFFFF' },
  obRow:  { flexDirection: 'row' },
  obCell: { paddingHorizontal: 4, paddingVertical: 3, fontSize: 8, fontFamily: 'NotoSans', fontWeight: 700 },
});

const COLS = [
  { label: 'Date',           flex: 0.9 },
  { label: 'Voucher No',     flex: 1.0 },
  { label: 'Payee / Member', flex: 1.2 },
  { label: 'Chq./Ref.No.',   flex: 1.0 },
  { label: 'Narration',      flex: 2.0 },
  { label: 'Dr (Rs.)',       flex: 1,   align: 'right' },
  { label: 'Cr (Rs.)',       flex: 1,   align: 'right' },
  { label: 'Balance (Rs.)',  flex: 1.1, align: 'right' },
];

export default function BankBookDocument({ data, theme: themeKey, orientation, sections, filters }) {
  const t = PDF_THEMES[themeKey] || PDF_THEMES[DEFAULT_THEME];

  // bankGroups is pre-computed by ReportsPage.js and passed directly
  const bankGroups = Array.isArray(data?.bankGroups) ? data.bankGroups : [];

  return (
    <Document>
      <Page size="A4" orientation={orientation || 'landscape'} style={[s.page, { backgroundColor: t.white }]}>
        {sections.header && <PdfHeader derasar={data.derasar} reportTitle="Bank Book" theme={t} />}
        <View style={s.content}>
          {sections.filterLine && <PdfFilterLine filters={filters} theme={t} />}
          {sections.summaryCards && (
            <PdfSummaryCards theme={t} items={[
              { label: 'Total Opening Balance', value: fmtINR(data.openingBalance), color: t.body },
              { label: 'Total Closing Balance', value: fmtINR(data.closingBalance), color: t.statusGreen },
            ]} />
          )}

          {bankGroups.map((group, gi) => {
            const { account, rows, openingBalance, closingBalance, totalDr, totalCr } = group;
            const bankName = account ? account.name : (group.bankName || 'Bank');
            return (
              <View key={gi}>
                {/* Bank heading bar */}
                <View style={[s.bankHeading, { backgroundColor: t.primary }]}>
                  <Text style={s.bankHeadingText}>{bankName}</Text>
                </View>

                {/* Opening balance row */}
                <View style={[s.obRow, { backgroundColor: t.mid }]}>
                  <Text style={[s.obCell, { flex: 7.6 }]}>Opening Balance</Text>
                  <Text style={[s.obCell, { flex: 1.1, textAlign: 'right' }]}>{fmtINR(openingBalance)}</Text>
                </View>

                <PdfTableHead cols={COLS} theme={t} />
                {(rows || []).map((r, i) => (
                  <PdfTableRow key={i} cols={COLS} index={i} theme={t} values={[
                    fmtDate(r.date),
                    r.voucherNo    || '—',
                    r.payeeName    || '—',
                    r.referenceNo  || '—',
                    r.narration    || '—',
                    r.debit  ? fmtINR(r.debit)  : '—',
                    r.credit ? fmtINR(r.credit) : '—',
                    fmtINR(r.runBal),
                  ]} />
                ))}
                <PdfTableFoot cols={COLS} theme={t} values={[
                  'Closing Balance', '', '', '', '', fmtINR(totalDr), fmtINR(totalCr), fmtINR(closingBalance),
                ]} />
              </View>
            );
          })}
        </View>
        {sections.footer && <PdfFooter theme={t} />}
      </Page>
    </Document>
  );
}
